import { type FC } from 'react';

interface PaginationProps {
  totalPages: number;
  currentPage: number;
  hasPrevPage?: boolean;
  hasNextPage?: boolean;
  onPageChange: (page: number) => void;
}

export const Pagination: FC<PaginationProps> = ({
  totalPages = 0,
  currentPage = 1,
  hasPrevPage = false,
  hasNextPage = false,
  onPageChange,
}) => {
  if (totalPages <= 1) return null; // hide pagination if only 1 page

  // 👇 Calculate visible page range (e.g. 8 9 10 11 12)
  const visiblePages = 5;
  const half = Math.floor(visiblePages / 2);
  let start = Math.max(currentPage - half, 1);
  let end = Math.min(start + visiblePages - 1, totalPages);

  if (end - start < visiblePages - 1) {
    start = Math.max(end - visiblePages + 1, 1);
  }

  const pages = Array.from({ length: end - start + 1 }, (_, i) => start + i);

  const handlePrev = () => {
    if (hasPrevPage && currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (hasNextPage && currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  return (
    <div className="flex items-center justify-between gap-2">
      {/* Previous Button */}
      <button
        onClick={handlePrev}
        disabled={!hasPrevPage || currentPage === 1}
        className={`px-3 py-3 flex items-center justify-center card text-text-primary transition ${
          !hasPrevPage || currentPage === 1
            ? 'opacity-50 cursor-not-allowed'
            : '!bg-black !text-white hover:opacity-80'
        }`}
      >
        <svg
          width="8"
          height="12"
          viewBox="0 0 8 12"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M6 12L0 6L6 0L7.4 1.4L2.8 6L7.4 10.6L6 12Z"
            fill={currentPage === 1 ? '#707070' : '#FFFFFF'}
          />
        </svg>
      </button>

      {/* Page Numbers */}
      {pages.map((page) => (
        <button
          key={page}
          onClick={() => onPageChange(page)}
          className={`px-3 py-2 flex items-center justify-center card transition ${
            page === currentPage
              ? '!border-[#5761EB] bg-[#5761EB]/10 text-[#5761EB]'
              : 'text-text-primary hover:bg-gray-100'
          }`}
        >
          {page}
        </button>
      ))}

      {/* Next Button */}
      <button
        onClick={handleNext}
        disabled={!hasNextPage || currentPage === totalPages}
        className={`px-3 py-3 flex items-center justify-center card text-text-primary transition ${
          !hasNextPage || currentPage === totalPages
            ? 'opacity-50 cursor-not-allowed'
            : '!bg-black !text-white hover:opacity-80'
        }`}
      >
        <svg
          width="8"
          height="12"
          viewBox="0 0 8 12"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M2 12L8 6L2 0L0.6 1.4L5.2 6L0.6 10.6L2 12Z"
            fill={currentPage === totalPages ? '#707070' : '#FFFFFF'}
          />
        </svg>
      </button>
    </div>
  );
};
